const RELOADED_KEY = 'orderbot.staleChunkReload';

/**
 * A tab opened before a deploy still references the old build's hashed chunk
 * names, which no longer exist on the server. When loading the chunk fails,
 * reload once so the tab picks up the new build instead of going blank. The
 * sessionStorage flag stops a reload loop if the chunk is truly unreachable
 * (offline, server down) — then the error surfaces as usual.
 */
export const withStaleChunkReload =
  <T>(load: () => Promise<T>, reload: () => void = () => window.location.reload()) =>
  (): Promise<T> =>
    load().then(
      (mod) => {
        try {
          sessionStorage.removeItem(RELOADED_KEY);
        } catch {
          // storage blocked — nothing to clear
        }
        return mod;
      },
      (err: unknown) => {
        let alreadyReloaded = true; // without storage a loop can't be ruled out, so don't reload
        try {
          alreadyReloaded = sessionStorage.getItem(RELOADED_KEY) === '1';
          if (!alreadyReloaded) sessionStorage.setItem(RELOADED_KEY, '1');
        } catch {
          // storage blocked — fall through and surface the error
        }
        if (alreadyReloaded) throw err;
        reload();
        return new Promise<T>(() => {}); // the page is going away; don't flash an error
      },
    );

/**
 * The signed-in app (antd, Redux, every screen) as its own chunk, so the
 * public landing page at "/" doesn't download it. One import site, so the
 * lazy route and the landing page's prefetch share the same chunk.
 */
export const loadApp = () => import('../App');

/** For the lazy route: same chunk, but recovers from a deploy that happened while the tab was open. */
export const loadAppForRoute = withStaleChunkReload(loadApp);
